import { debounce } from "lodash";
import { useListFilterContext } from "ra-core";
import { FC, createContext, useContext, useMemo, useState } from "react";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { FileTagResponseDto, SearchInput } from "~/gen/api/securefile";


interface SecureFilesTableSearchContextProps {
  states: {
    term: string;
    tags: FileTagResponseDto[];
    primaryClassifications: PrimaryClassification[];
    secondaryClassifications: SecondaryClassificationResponseDto[];
    startTime: Date | null;
    endTime: Date | null;
  };
  searchName: (s: string) => void;
  setSearchTag: (tag: FileTagResponseDto, remove: boolean) => void;
  setSearchSClss: (s: SecondaryClassificationResponseDto, remove: boolean) => void;
  setSearchPClss: (p: PrimaryClassification, remove: boolean) => void;
  setSearchTime: (start: Date | null, end: Date | null) => void;
}

export const SecureFilesTableSearchContext = createContext<SecureFilesTableSearchContextProps>({} as any)


export const SecureFilesTableSearchProvider : FC = ({ children }) => {
  const { setFilters, displayedFilters } = useListFilterContext()

  const [states, setStates] = useState({
    term: '',
    tags: [] as FileTagResponseDto[],
    primaryClassifications: [] as PrimaryClassification[],
    secondaryClassifications: [] as SecondaryClassificationResponseDto[],
    startTime: null as Date|null,
    endTime: null as Date|null,
  })

  const ctrls = useMemo(() => {
    const invokeSearch = (s1: typeof states) => {
      const input : SearchInput = {
        term: s1.term || null,
        tags: s1.tags.length > 0 ? s1.tags.map(t => t.tagId!) : null,
        primaryClassifications: s1.primaryClassifications.length > 0 ? s1.primaryClassifications : null,
        secondaryClassifications: s1.secondaryClassifications.length > 0 ? s1.secondaryClassifications.map(s => s.secondaryClassificationId!) : null,
        startTime: s1.startTime ? s1.startTime.toISOString() : null,
        endTime: s1.endTime ? s1.endTime.toISOString() : null,
      }
      setFilters(input, displayedFilters)
    }
  
    const debouncedInvokeSearch = debounce(invokeSearch, 300)
  
    return {
      searchName: (s: string) => {
        setStates(v => {
          if (v.term !== s) {
            const v1 = {...v, term: s}
            setTimeout(() => debouncedInvokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },  
      setSearchTag: (tag: FileTagResponseDto, remove: boolean) => {
        setStates(v => {
          const i = v.tags.findIndex(t => t.tagId === tag.tagId)
          if (remove && i > -1) {
            const tags = [...v.tags]
            tags.splice(i, 1)
            const v1 = {...v, tags }
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.tags.push(tag)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },  
      setSearchSClss: (s: SecondaryClassificationResponseDto, remove: boolean) => {
        setStates(v => {
          const i = v.secondaryClassifications.findIndex(x => x.secondaryClassificationId === s.secondaryClassificationId)
          if (remove && i > -1) {
            const secondaryClassifications = [...v.secondaryClassifications]
            secondaryClassifications.splice(i, 1)
            const v1 = {...v, secondaryClassifications }
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.secondaryClassifications.push(s)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },  
      setSearchTime: (start:Date|null, end:Date|null) => {
        setStates(v => {
          if (v.startTime === start && v.endTime === end) {
            return v
          } else {
            const v1 = {...v, startTime: start, endTime: end}
            setTimeout(() => invokeSearch(v1))
            return v1
          }
        })
      },  
      setSearchPClss: (p: PrimaryClassification, remove: boolean) => {
        setStates(v => {
          const i = v.primaryClassifications.findIndex(x => x == p)
          if (remove && i > -1) {
            const primaryClassifications = [...v.primaryClassifications]
            primaryClassifications.splice(i, 1)
            const v1 = {...v, primaryClassifications }
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.primaryClassifications.push(p)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },
    }
  }, [setFilters, displayedFilters])

  return (
    <SecureFilesTableSearchContext.Provider value={{...ctrls, states}}>
      {children}
    </SecureFilesTableSearchContext.Provider>
  )
}

export const useSecureFilesTableSearchContext = () => useContext(SecureFilesTableSearchContext)